/**
 * Aurora-style tinted status pill.
 *
 *   <StatusPill tone="green">+4.33%</StatusPill>
 *   <StatusPill tone="amber" leadingDot>WARN</StatusPill>
 *   <StatusPill tone="red"   icon={<TrendingDown />}>−12.5%</StatusPill>
 *
 * Pattern: 12% alpha background + solid tint color text, small + tight
 * padding. Replaces a sea of one-off `Chip` styles across the codebase with a
 * single helper that matches Aurora's "small actionable pill" idiom.
 */

import React from 'react';
import { Box } from '@mui/material';

const TONE_COLOR = {
  green:   '#22c55e',
  red:     '#ef4444',
  amber:   '#f59e0b',
  blue:    '#3b82f6',
  purple:  '#E9B065',
  teal:    '#D98A2B',
  neutral: '#6b7280',
};

export default function StatusPill({
  tone = 'neutral',
  children,
  icon,
  leadingDot = false,
  size = 'sm',          // 'sm' | 'md'
  variant = 'tinted',   // 'tinted' | 'solid' | 'outlined'
  sx = {},
  ...rest
}) {
  const c = TONE_COLOR[tone] || TONE_COLOR.neutral;

  const sizeStyles = size === 'md'
    ? { fontSize: '0.78rem', px: 1.1, py: 0.4 }
    : { fontSize: '0.70rem', px: 0.85, py: 0.25 };

  const variantStyles =
    variant === 'solid'
      ? { bgcolor: c, color: '#0E0F0C' }
      : variant === 'outlined'
        ? { bgcolor: 'transparent', color: c, border: `1px solid ${c}66` }
        : { bgcolor: `${c}1f`, color: c };  // tinted (default) — 12% alpha

  return (
    <Box
      component="span"
      sx={{
        display:    'inline-flex',
        alignItems: 'center',
        gap:        0.4,
        borderRadius: '6px',
        fontWeight: 700,
        letterSpacing: '0.01em',
        lineHeight: 1,
        whiteSpace: 'nowrap',
        fontVariantNumeric: 'tabular-nums',
        ...sizeStyles,
        ...variantStyles,
        ...sx,
      }}
      {...rest}
    >
      {leadingDot && (
        <Box component="span" sx={{
          width: 6, height: 6, borderRadius: '50%', bgcolor: c, opacity: 0.9,
        }} />
      )}
      {icon}
      {children}
    </Box>
  );
}
